import { useMemo } from 'react';
import { formInputClassName, secondaryTextClassName, Panel } from '@monecolier/ui';
import type { ClassLevel, SchoolClass, SchoolYear } from '@monecolier/api-client';
import { CLASS_LEVELS } from '@monecolier/types';
import { classCapacityStatus, toneBadge } from '../lib/classCapacityStatus';

const LEVEL_ORDER = new Map(CLASS_LEVELS.map((level, index) => [level, index]));

interface ClassHeadcountPanelProps {
  classesByLevel: Partial<Record<ClassLevel, SchoolClass[]>>;
  schoolYears: SchoolYear[];
  selectedSchoolYearLabel: string | null;
  onSelectedSchoolYearLabelChange: (label: string) => void;
  // UX-74 : niveau de titre du Panel — ce composant a DEUX contextes de page
  // réels (voir les deux appelants) : direct sous le <h1> de StudentsOverview
  // (défaut Panel, h2) vs sous le <h2> "Bonjour" de Home.tsx (h3, passé
  // explicitement par cet appelant). Jamais un seul niveau fixe qui serait
  // faux dans l'un des deux contextes.
  titleAs?: 'h2' | 'h3' | 'h4';
}

/**
 * UX-44 — EXTRAIT de `StudentsOverview.tsx` (UX-18/UX-35, seule
 * implémentation avant ce ticket) : `Home.tsx` en devient le SECOND
 * consommateur réel — même précédent que `classCapacityStatus.tsx`/
 * `enrolledStudentsSummary.ts`, jamais une seconde implémentation
 * divergente du tableau "Effectif par classe" (seuils/tri de classe
 * STRICTEMENT INCHANGÉS, voir `../lib/classCapacityStatus.tsx`).
 *
 * Reçoit ses données EN PROPS (pas de fetch propre) — l'appelant décide de
 * QUEL appel réseau les alimente (`GET /classes`, déjà partagé avec
 * d'autres besoins de la page appelante), ce composant ne fait que
 * dériver/rendre.
 */
export function ClassHeadcountPanel({
  classesByLevel,
  schoolYears,
  selectedSchoolYearLabel,
  onSelectedSchoolYearLabelChange,
  titleAs,
}: ClassHeadcountPanelProps) {
  const classRows = useMemo(() => {
    if (!selectedSchoolYearLabel) return [];
    return Object.values(classesByLevel)
      .flat()
      .filter(
        (schoolClass): schoolClass is SchoolClass => !!schoolClass && schoolClass.schoolYear === selectedSchoolYearLabel,
      )
      .sort((a, b) => {
        const levelDiff = (LEVEL_ORDER.get(a.level) ?? 0) - (LEVEL_ORDER.get(b.level) ?? 0);
        return levelDiff !== 0 ? levelDiff : a.name.localeCompare(b.name);
      });
  }, [classesByLevel, selectedSchoolYearLabel]);

  return (
    <Panel title="Effectif par classe" titleAs={titleAs}>
      {schoolYears.length === 0 ? (
        <p className={secondaryTextClassName} data-testid="class-headcount-no-school-year">
          Aucune année scolaire créée pour le moment.
        </p>
      ) : (
        <>
          <label className="mb-3 flex items-center justify-end gap-2">
            <span className="sr-only">Année scolaire</span>
            <select
              value={selectedSchoolYearLabel ?? ''}
              onChange={(event) => onSelectedSchoolYearLabelChange(event.target.value)}
              className={formInputClassName}
              data-testid="class-headcount-school-year-select"
            >
              {schoolYears.map((year) => (
                <option key={year.id} value={year.label}>
                  {year.label}
                </option>
              ))}
            </select>
          </label>
          {classRows.length === 0 ? (
            <p className={secondaryTextClassName} data-testid="class-headcount-empty">
              Aucune classe pour {selectedSchoolYearLabel}.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[28rem] text-sm" data-testid="class-headcount-table">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase text-gray-500">
                    <th className="py-1 pr-2">Classe</th>
                    <th className="py-1 pr-2">Effectif</th>
                    <th className="py-1 pr-2">Places restantes</th>
                    <th className="py-1">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {classRows.map((schoolClass) => {
                    const status = classCapacityStatus(schoolClass.capacity, schoolClass.studentCount);
                    return (
                      <tr
                        key={schoolClass.id}
                        className="border-b border-gray-100 last:border-0"
                        data-testid={`class-headcount-row-${schoolClass.id}`}
                      >
                        <td className="py-1 pr-2">{schoolClass.name}</td>
                        <td className="py-1 pr-2">{schoolClass.studentCount}</td>
                        <td className="py-1 pr-2">{status.remainingLabel}</td>
                        <td className="py-1">{toneBadge(status.label, status.tone)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
